import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface SongsData {
  title?: string;
  songs?: string[];
}

interface SongsExperienceProps {
  darkMode: boolean;
  data?: SongsData;
  onDataChange?: (data: SongsData) => void;
}

const MAX_SONGS = 10;

const normalizeSongs = (songs?: string[]) => {
  const normalized = (songs && songs.length > 0 ? songs : [""])
    .slice(0, MAX_SONGS)
    .map((song) => song ?? "");
  return normalized;
};

const SongsExperience: React.FC<SongsExperienceProps> = ({
  darkMode,
  data,
  onDataChange,
}) => {
  const { t } = useTranslation();
  const [songs, setSongs] = useState<string[]>(() =>
    normalizeSongs(data?.songs)
  );
  const lastSyncedRef = useRef<string>("");
  const lastSentRef = useRef<string>("");
  const onDataChangeRef = useRef<SongsExperienceProps["onDataChange"]>();

  const title = useMemo(
    () => t("add_songs", { ns: "taascard" }) || "Add songs",
    [t]
  );

  useEffect(() => {
    const normalized = normalizeSongs(data?.songs);
    const serialized = JSON.stringify(normalized);
    if (serialized !== lastSyncedRef.current) {
      setSongs(normalized);
      lastSyncedRef.current = serialized;
    }
  }, [data?.songs]);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    if (!onDataChangeRef.current) return;
    const payload = { title, songs };
    const serialized = JSON.stringify(payload);
    if (serialized === lastSentRef.current) return;
    lastSentRef.current = serialized;
    onDataChangeRef.current(payload);
  }, [songs, title]);

  const handleSongChange = (index: number, value: string) => {
    setSongs((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleAddSong = () => {
    setSongs((prev) => {
      if (prev.length >= MAX_SONGS) return prev;
      return [...prev, ""];
    });
  };

  const handleRemoveSong = (index: number) => {
    setSongs((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        p: 3,
        gap: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: darkMode ? "var(--neutralWhite)" : "var(--neutral-900)",
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {songs.map((song, index) => (
          <Box
            key={`song-${index}`}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <TextField
              label={`${t("song_name", { ns: "taascard" }) || "Song name"} ${
                index + 1
              }`}
              value={song}
              onChange={(e) => handleSongChange(index, e.target.value)}
              fullWidth
              placeholder={t("song_name", { ns: "taascard" }) || "Song name"}
              sx={{
                "& .MuiInputLabel-root": {
                  color: darkMode ? "var(--neutral-400)" : "var(--neutral-600)",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: darkMode ? "var(--primary-400)" : "var(--primary-500)",
                },
                "& .MuiOutlinedInput-root": {
                  bgcolor: darkMode
                    ? "var(--neutral-800)"
                    : "var(--neutralWhite)",
                  color: darkMode ? "var(--neutral-100)" : "var(--neutral-900)",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: darkMode
                    ? "var(--neutral-700)"
                    : "var(--neutral-300)",
                },
                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: darkMode
                      ? "var(--neutral-600)"
                      : "var(--neutral-400)",
                  },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: darkMode
                      ? "var(--primary-500)"
                      : "var(--primary-500)",
                  },
                "& input::placeholder": {
                  color: darkMode ? "var(--neutral-500)" : "var(--neutral-400)",
                  opacity: 1,
                },
              }}
            />
            {songs.length > 1 && (
              <IconButton
                aria-label={t("delete", { ns: "taascard" }) || "Delete"}
                onClick={() => handleRemoveSong(index)}
                sx={{
                  color: darkMode
                    ? "var(--destructive-400)"
                    : "var(--destructive-600)",
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 1 }}>
        <Button
          variant="outlined"
          onClick={handleAddSong}
          disabled={songs.length >= MAX_SONGS}
          startIcon={<AddIcon />}
          sx={{
            textTransform: "none",
            borderColor: darkMode ? "var(--neutral-600)" : "var(--neutral-300)",
            color: darkMode ? "var(--neutral-100)" : "var(--neutral-900)",
          }}
        >
          {t("add_song", { ns: "taascard" }) || "Add song"}
        </Button>
      </Box>
    </Box>
  );
};

export default SongsExperience;
